/**
 * 오브젝트 스포너 (FR-001/013, data-model §2).
 * 카탈로그 def → 프리미티브 메쉬 + Rapier 바디를 책상 위 SPAWN_SLOTS에 배치.
 * 리셋은 전부 제거 후 SPAWN_SLOTS에서 초기 배치를 재생성한다.
 */
import * as THREE from 'three';
import type { BreakableObjectDef, ObjectState, PrimitiveSpec } from '../core/types';
import type { PhysicsWorld } from '../physics/PhysicsWorld';
import { getObjectDef } from '../config/objects';
import { LAYOUT, SPAWN_SLOTS } from '../config/layout';
import { boundingBoxOf } from '../physics/fracture';
import { buildObjectModel } from '../render/models';

export interface SpawnedObject {
  instanceId: number;
  def: BreakableObjectDef;
  body: import('@dimforge/rapier3d-compat').RigidBody;
  /** 상세 모델 Group (여러 파트로 조립). */
  mesh: THREE.Object3D;
  state: ObjectState;
  /** 같은 def를 공유하는 시각 변형(모니터 'code'/'browser') — 파편 팔레트 분기용. */
  variant?: 'code' | 'browser';
}

export class ObjectSpawner {
  objects = new Map<number, SpawnedObject>();
  private nextId = 1;

  constructor(
    private physics: PhysicsWorld,
    private scene: THREE.Scene,
    private models?: Map<string, THREE.Group>,
  ) {}

  /** 책상 위 초기 배치. */
  spawnAll(): void {
    for (const slot of SPAWN_SLOTS) {
      const def = getObjectDef(slot.defId);
      const bbox = boundingBoxOf(def.primitive);
      const restY = LAYOUT.desk.topY + bbox[1] / 2 + 0.002;
      this.spawnOne(def, new THREE.Vector3(slot.x, restY, slot.z));
    }
  }

  private spawnOne(def: BreakableObjectDef, pos: THREE.Vector3): SpawnedObject {
    const instanceId = this.nextId++;
    // 프리로드된 GLB가 있으면 클론, 없으면 절차적 모델(폴백).
    const preloaded = this.models?.get(def.id);
    const mesh = preloaded ? preloaded.clone(true) : buildObjectModel(def);
    mesh.userData.instanceId = instanceId; // 레이캐스트 소유자 역추적용
    mesh.position.copy(pos);
    this.scene.add(mesh);

    const body = this.physics.addDynamicBody({ x: pos.x, y: pos.y, z: pos.z });
    this.physics.attachCollider(body, colliderDesc(this.physics, def.primitive), def.mass, {
      kind: 'object',
      instanceId,
      defId: def.id,
    });

    const obj: SpawnedObject = {
      instanceId,
      def,
      body,
      mesh,
      state: 'intact',
      variant: mesh.userData.variant as 'code' | 'browser' | undefined,
    };
    this.objects.set(instanceId, obj);
    return obj;
  }

  /** 매 프레임 시각 동기화(파괴되지 않은 오브젝트). */
  syncMeshes(): void {
    for (const obj of this.objects.values()) {
      if (obj.state === 'broken') continue;
      const t = obj.body.translation();
      const r = obj.body.rotation();
      obj.mesh.position.set(t.x, t.y, t.z);
      obj.mesh.quaternion.set(r.x, r.y, r.z, r.w);
    }
  }

  /** 파괴 표시: 메쉬 숨김 + 바디 제거. */
  markBroken(instanceId: number): void {
    const obj = this.objects.get(instanceId);
    if (!obj || obj.state === 'broken') return;
    obj.state = 'broken';
    obj.mesh.visible = false;
    this.physics.removeBody(obj.body);
  }

  /** 온전한(부술 수 있는) 오브젝트 수. */
  get intactCount(): number {
    let n = 0;
    for (const o of this.objects.values()) if (o.state !== 'broken') n++;
    return n;
  }

  /** 리셋: 전부 제거 후 초기 배치 재생성 (FR-013). */
  resetAll(): void {
    for (const obj of this.objects.values()) {
      if (obj.state !== 'broken') this.physics.removeBody(obj.body);
      this.scene.remove(obj.mesh);
      // 지오메트리·머티리얼은 models.ts 모델 캐시(프로토타입)와 공유되므로 dispose 금지 —
      // 리셋은 O(배치 복원)으로 끝난다(재빌드·GPU 재업로드 없음).
    }
    this.objects.clear();
    this.spawnAll();
  }
}

function colliderDesc(physics: PhysicsWorld, spec: PrimitiveSpec): import('@dimforge/rapier3d-compat').ColliderDesc {
  const R = physics.RAPIER;
  switch (spec.kind) {
    case 'box':
      return R.ColliderDesc.cuboid(spec.size[0] / 2, spec.size[1] / 2, spec.size[2] / 2);
    case 'cylinder':
      return R.ColliderDesc.cylinder(spec.height / 2, spec.radius);
    case 'sphere':
      return R.ColliderDesc.ball(spec.radius);
  }
}
